import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';
import { exportRowsToExcel } from '../utils/exportExcel';
import './VarianceReport.css';

function inventoryPageUrl(page, limit, isAdmin, selectedLocationId) {
  const p = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    q: '',
  });
  if (isAdmin && selectedLocationId !== '') {
    p.set('consignment_location_id', selectedLocationId);
  }
  return `/api/inventory?${p.toString()}`;
}

const StockReport = () => {
  const navigate = useNavigate();
  const { fetchWithAuth, user, isAdmin } = useAuth();
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadLocations = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await fetchWithAuth('/api/consignment-locations');
      if (res.ok) {
        const list = await res.json();
        setLocations(Array.isArray(list) ? list : []);
      }
    } catch {
      setLocations([]);
    }
  }, [fetchWithAuth, isAdmin]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const loadRecords = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const all = [];
      let page = 1;
      const limit = 200;
      for (;;) {
        const res = await fetchWithAuth(inventoryPageUrl(page, limit, isAdmin, selectedLocationId));
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || 'Failed to load stock');
        }
        const data = await res.json();
        const items = Array.isArray(data.items) ? data.items : [];
        all.push(...items);
        const total = Number(data.total) || 0;
        if (items.length < limit || all.length >= total) break;
        page += 1;
      }
      setRows(all);
    } catch (err) {
      setError(err.message || 'Failed to load stock');
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth, isAdmin, selectedLocationId]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleExport = () => {
    const exportRows = rows.map((r) => ({
      'Part number': r.partNumber || '',
      Description: r.partDescription || '',
      Quantity: r.quantity != null ? String(r.quantity) : '',
      'Bin location': r.binLocation || '',
      Created: r.createdAt ? new Date(r.createdAt).toLocaleString() : '',
      Updated: r.updatedAt ? new Date(r.updatedAt).toLocaleString() : '',
    }));
    const stamp = new Date().toISOString().slice(0, 10);
    exportRowsToExcel(`stock-report-${stamp}`, 'Stock', exportRows);
  };

  return (
    <Layout>
      <div className="variance-container">
        <div className="variance-header">
          <div className="variance-header-left">
            <button type="button" className="variance-back" onClick={() => navigate('/reports')}>
              ← Back to Reports
            </button>
            <h1>Stock report</h1>
          </div>
          <div className="variance-header-actions">
            {isAdmin && (
              <label className="variance-filter">
                <span className="variance-filter-label">Location:</span>
                <select
                  value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  className="variance-filter-select"
                >
                  <option value="">All locations</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name || loc.code || `Location #${loc.id}`}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {!isAdmin && user?.consignmentLocationName && (
              <span className="variance-badge">Location: {user.consignmentLocationName}</span>
            )}
            <button type="button" className="variance-refresh" onClick={loadRecords}>
              🔄 Refresh
            </button>
            <button type="button" className="variance-refresh" onClick={handleExport} disabled={rows.length === 0}>
              ⬇ Excel
            </button>
          </div>
        </div>
        <p className="variance-subtitle">
          On-hand inventory by part and bin (same data as Inventory; export includes all rows for the current location
          filter).
        </p>
        {error && <div className="variance-error">{error}</div>}
        {isLoading ? (
          <div className="variance-loading">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="variance-empty">
            <div className="variance-empty-icon">📊</div>
            <h2>No stock lines</h2>
            <p>No inventory for this filter yet.</p>
          </div>
        ) : (
          <div className="variance-table-wrap">
            <table className="variance-table">
              <thead>
                <tr>
                  <th>Part number</th>
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Bin</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.partNumber || '—'}</td>
                    <td className="variance-desc">{(r.partDescription || '—').slice(0, 48)}</td>
                    <td className="variance-num">{r.quantity}</td>
                    <td>{r.binLocation || '—'}</td>
                    <td className="variance-date">
                      {r.updatedAt ? new Date(r.updatedAt).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StockReport;
